import { useCallback, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Toolbar } from "../../../../../utils/ui/Toolbar";
import { Icon } from "../../../../../utils/ui/Icon";
import { ColorButton } from "./ColorButton";

export type ColorPickerProps = {
  color?: string;
  onChange?: (color: string) => void;
  onClear?: () => void;
  /** Most-recently-used colours (most recent first) shown as swatch buttons — see DMS-2004 item 7. */
  recentColors?: string[];
  /** Shows a permanent "no colour" swatch above the recent-colours grid — for contexts where an
   * explicit transparent/no-background state is meaningful (table cell/row/column background),
   * not text or line colour, where it isn't. Kept separate from recentColors since it's a fixed
   * choice, not a history entry. */
  showClearSwatch?: boolean;
  /** True when no colour is actually set (as opposed to `color` above, which the caller may have
   * defaulted for the hex picker's sake) — used only to mark the clear swatch active. */
  isColorUnset?: boolean;
};

export const ColorPicker = ({
  color,
  onChange,
  onClear,
  recentColors,
  showClearSwatch,
  isColorUnset,
}: ColorPickerProps) => {
  const [colorInputValue, setColorInputValue] = useState(color || "");

  const handleColorUpdate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setColorInputValue(event.target.value);
  }, []);

  const handleColorChange = useCallback(() => {
    const isCorrectColor = /^#([0-9A-F]{3}){1,2}$/i.test(colorInputValue);

    if (!isCorrectColor) {
      if (onChange) {
        onChange("");
      }

      return;
    }

    if (onChange) {
      onChange(colorInputValue);
    }
  }, [colorInputValue, onChange]);

  return (
    <div className="flex flex-col gap-2">
      <HexColorPicker className="w-full" color={color || ""} onChange={onChange} />
      <input
        type="text"
        className="w-full p-2 text-black bg-white border rounded dark:bg-black dark:text-white border-neutral-200 dark:border-neutral-800 focus:outline-1 focus:ring-0 focus:outline-neutral-300 dark:focus:outline-neutral-700"
        placeholder="#000000"
        value={colorInputValue}
        onChange={handleColorUpdate}
        onBlur={handleColorChange}
      />
      {showClearSwatch && (
        <div className="flex items-center gap-1">
          <ColorButton isClearSwatch active={isColorUnset} onColorChange={onChange} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 max-w-[13rem]">
        {recentColors?.map((recentColor) => (
          <ColorButton
            active={recentColor === color}
            color={recentColor}
            key={recentColor}
            onColorChange={onChange}
          />
        ))}
      </div>
      <Toolbar.Button tooltip="Reset color to default" onClick={onClear}>
        <Icon name="Undo" />
      </Toolbar.Button>
    </div>
  );
};
